/**
 * 5% Compliance Dashboard Widget
 * 
 * Real-time compliance gauge showing CMS 5% volunteer rule status.
 * Displays percentage, alert level, and quick actions.
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  CheckCircle, AlertTriangle, AlertCircle, TrendingUp, 
  Users, Clock, ChevronRight, Plus, RefreshCw
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Gauge component for visual compliance display
function ComplianceGauge({ percentage, target, isDark }) {
  const isCompliant = percentage >= target;
  const gaugeColor = isCompliant ? "#22c55e" : percentage >= target * 0.8 ? "#f59e0b" : "#ef4444";
  const rotation = Math.min((percentage / 10) * 180, 180); // Scale to 180 degrees max

  return (
    <div className="relative w-32 h-20 mx-auto">
      {/* Background arc */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 60">
        <path
          d="M 10 55 A 40 40 0 0 1 90 55"
          fill="none"
          stroke={isDark ? "#334155" : "#e2e8f0"}
          strokeWidth="8"
          strokeLinecap="round"
        />
        {/* Foreground arc */}
        <path
          d="M 10 55 A 40 40 0 0 1 90 55"
          fill="none"
          stroke={gaugeColor}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${(rotation / 180) * 126} 126`}
          style={{ transition: "stroke-dasharray 0.5s ease-out" }}
        />
        {/* Target marker at 5% */}
        <line
          x1="50"
          y1="15"
          x2="50"
          y2="25"
          stroke={isDark ? "#94a3b8" : "#64748b"}
          strokeWidth="2"
          transform={`rotate(${(target / 10) * 180 - 90}, 50, 55)`}
        />
      </svg>
      
      {/* Center percentage */}
      <div className="absolute inset-0 flex items-end justify-center pb-1">
        <span className="text-2xl font-bold" style={{ color: gaugeColor }}>
          {percentage.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

export function FivePercentComplianceWidget({ isDark, compact = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("hospice_token");
      const res = await fetch(`${API_URL}/api/volunteer-management/alerts/five-percent-status`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        setData(result);
        setError(null);
      } else {
        setError("Failed to load compliance data");
      }
    } catch (err) {
      console.error("Error fetching 5% compliance:", err);
      setError("Connection error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !data) {
    return (
      <div className={`p-4 rounded-lg animate-pulse ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
        <div className="h-20 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-teal-500" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
        <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>{error}</p>
      </div>
    );
  }

  const percentage = data?.volunteer_percentage || 0;
  const target = data?.target_percentage || 5.0;
  const isCompliant = data?.meets_requirement;
  const alertLevel = data?.alert_level || "none";

  // Compact version for sidebar
  if (compact) {
    return (
      <div 
        className={`p-3 rounded-lg cursor-pointer transition-all ${
          isDark 
            ? "bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50" 
            : "bg-gray-100 hover:bg-gray-200 border border-gray-200"
        } ${!isCompliant ? "border-red-500/30" : "border-green-500/30"}`}
        onClick={() => navigate("/volunteer-management")}
        data-testid="five-percent-widget"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <TrendingUp className={`w-4 h-4 ${isCompliant ? "text-green-500" : "text-red-500"}`} />
            <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
              5% Rule
            </span>
          </div>
          <Badge className={`text-xs ${
            isCompliant 
              ? "bg-green-500/20 text-green-400 border-0" 
              : "bg-red-500/20 text-red-400 border-0"
          }`}>
            {isCompliant ? "Compliant" : "Action Needed"}
          </Badge>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Progress 
              value={Math.min(percentage * 10, 100)} 
              className={`h-2 ${isCompliant ? "" : "[&>div]:bg-red-500"}`}
            />
          </div>
          <span className={`text-lg font-bold ${isCompliant ? "text-green-500" : "text-red-500"}`}>
            {percentage.toFixed(1)}%
          </span>
        </div>
        
        {!isCompliant && data?.gap_analysis?.hours_needed_for_compliance > 0 && (
          <p className={`text-xs mt-2 ${isDark ? "text-red-400" : "text-red-600"}`}>
            Need {data.gap_analysis.hours_needed_for_compliance.toFixed(0)}h more
          </p>
        )}
      </div>
    );
  }

  // Full card version
  return (
    <Card 
      className={`cursor-pointer transition-all ${
        isDark 
          ? "bg-slate-900 border-slate-800 hover:border-teal-500/50" 
          : "bg-white hover:border-teal-500"
      } ${!isCompliant ? "border-red-500/30" : ""}`}
      onClick={() => navigate("/volunteer-management")}
      data-testid="five-percent-widget-full"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isCompliant ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : alertLevel === "critical" ? (
              <AlertCircle className="w-4 h-4 text-red-500" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
            )}
            CMS 5% Volunteer Rule
          </div>
          <ChevronRight className={`w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Gauge */}
        <ComplianceGauge percentage={percentage} target={target} isDark={isDark} />
        
        {/* Status */}
        <div className="text-center mt-2">
          <Badge className={`${
            isCompliant 
              ? "bg-green-500/20 text-green-400" 
              : alertLevel === "critical"
                ? "bg-red-500/20 text-red-400"
                : "bg-yellow-500/20 text-yellow-400"
          } border-0`}>
            {isCompliant ? "COMPLIANT" : alertLevel === "critical" ? "CRITICAL" : "NON-COMPLIANT"}
          </Badge>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <div className={`text-center p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
            <p className="text-xs text-gray-500">Volunteer Hrs</p>
            <p className="font-bold text-teal-500">{data?.hours_summary?.volunteer_hours || 0}</p>
          </div>
          <div className={`text-center p-2 rounded ${isDark ? "bg-slate-800" : "bg-gray-50"}`}>
            <p className="text-xs text-gray-500">Staff Hrs</p>
            <p className="font-bold text-blue-500">{data?.hours_summary?.paid_staff_hours || 0}</p>
          </div>
        </div>
        
        {/* Gap Analysis */}
        {!isCompliant && data?.gap_analysis && (
          <div className={`mt-3 p-2 rounded-lg text-center ${isDark ? "bg-red-500/10" : "bg-red-50"}`}>
            <p className="text-xs text-red-500">
              Need <strong>{data.gap_analysis.hours_needed_for_compliance?.toFixed(1) || 0}h</strong> more 
              ({data.gap_analysis.additional_visits_needed || 0} visits)
            </p>
          </div>
        )}
        
        {/* Quick Actions */}
        {!isCompliant && (
          <div className="flex gap-2 mt-3">
            <Button 
              size="sm" 
              className="flex-1 bg-teal-600 hover:bg-teal-700 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/volunteer-management?tab=visits&action=add");
              }}
            >
              <Plus className="w-3 h-3 mr-1" />
              Log Visit
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="flex-1 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/volunteer-management?tab=volunteers");
              }}
            >
              <Users className="w-3 h-3 mr-1" />
              Volunteers
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default FivePercentComplianceWidget;
